import { useCallback, useMemo } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type FormItem } from "@njbsoft/form-logic";
import { FormLetterhead, FormRenderer, parseFormDefinition } from "@njbsoft/form-renderer";
import { resolveFormTheme } from "@njbsoft/form-ui";
import {
  Badge,
  Button,
  Card,
  EmptyValue,
  PageHeader,
  PageSkeleton,
  portalStatusVariant,
} from "@/components/ui";
import {
  submissionOutcomeLabelKey,
  submissionOutcomeVariant,
  templateTitleOrFallback,
} from "@/components/tester/history/history-columns";
import { CompanyReportFormCard } from "@/components/company/submissions/company-report-form-card";
import { CompanySubmissionAttachmentGallery } from "@/components/company/submissions/company-submission-attachment-gallery";
import { useActiveMembership } from "@/hooks/use-company-context";
import { useCompanyTestSubmission } from "@/hooks/use-company-test-submissions";
import { formatDateTime, formatPerformedAt } from "@/lib/dates";
import type { CompanyTestSubmissionAttachmentMeta } from "@/hooks/use-company-test-submissions";

export const Route = createFileRoute("/_company/company/submissions/$submissionId")({
  component: CompanySubmissionDetailPage,
});

/**
 * One regulatory document a member of the company filed, reopened read-only.
 * The tester's own drill-in minus its two tester-only affordances: there is no
 * Resubmit here — a correction is a new test and belongs to the performing
 * tester through the field stack — and no GPS block, because the company reads
 * a regulatory record, not the tester's collection coordinates.
 *
 * THE DOCUMENT IS RENDERED SEALED, and the mechanism is a single OMITTED prop.
 * `FormRenderer` treats an absent `onAnswerChange` as "this mount is displaying
 * a finished document rather than collecting one", and that one fact is what
 * makes it read the outcome and every computed value SEALED INTO the responses
 * at submit instead of re-deriving them from today's evaluator. Adding a
 * handler here — even a no-op, even "for symmetry" — silently converts a signed
 * regulatory record into a live recomputation, with nothing failing to say so.
 * The suite asserts the prop's absence for exactly that reason.
 *
 * The chrome around it prints. The page gutter collapses for print because on
 * paper it would sit INSIDE the stylesheet's `@page` margin and push the
 * document a further 2rem in on every edge of every sheet; the identity list
 * and the actions drop out, leaving the letterhead + document alone on the
 * sheet.
 */
function CompanySubmissionDetailPage() {
  const { t, i18n } = useTranslation([
    "company",
    "field-work",
    "form-renderer",
    "form-submissions",
  ]);
  const { submissionId } = Route.useParams();
  const active = useActiveMembership();
  const companyId = active?.company_id ?? "";
  const { data: detail, isLoading, isError } = useCompanyTestSubmission(companyId, submissionId);

  const attachmentsByLinkId = useMemo(() => {
    const map = new Map<string, CompanyTestSubmissionAttachmentMeta[]>();
    for (const att of detail?.attachments ?? []) {
      const bucket = map.get(att.link_id);
      if (bucket) {
        bucket.push(att);
      } else {
        map.set(att.link_id, [att]);
      }
    }
    return map;
  }, [detail?.attachments]);

  const evidenceComplete = detail?.attachments_complete ?? true;

  const attachmentSlot = useCallback(
    (item: FormItem) => (
      <CompanySubmissionAttachmentGallery
        companyId={companyId}
        submissionId={submissionId}
        attachments={attachmentsByLinkId.get(item.link_id) ?? []}
        evidenceComplete={evidenceComplete}
      />
    ),
    [companyId, submissionId, attachmentsByLinkId, evidenceComplete],
  );

  if (isLoading) {
    return (
      <div className="p-8">
        <PageSkeleton />
      </div>
    );
  }

  // Visibility is undifferentiated 404: another company's document, an
  // independent tester's, a nonexistent id, and a real document in a water
  // system whose authorization edge lapsed all answer identically, so there is
  // nothing more specific to branch on.
  if (isError || !detail) {
    return (
      <div className="space-y-4 p-8">
        <PageHeader title={t("company:submissions.detail.title")} />
        <p className="text-sm text-muted">{t("company:submissions.detail.notFound")}</p>
        <Link to="/company/submissions" className="text-sm text-primary hover:underline">
          {t("company:submissions.detail.back")}
        </Link>
      </div>
    );
  }

  const language: "en" | "es" = i18n.language.startsWith("es") ? "es" : "en";

  // The wire definition is an opaque JSON object; the pinned version passed the
  // server's publish gates, but guard on `sections` before handing the document
  // to the renderer.
  const definition = parseFormDefinition(detail.definition);
  const renderable = Array.isArray(definition.sections);

  const formTitle = templateTitleOrFallback(t, detail.template_title);
  // Severed together with the title by the same LEFT JOIN, so it takes the same
  // stand-in rather than leaving the letterhead's meta line blank.
  const versionLine =
    detail.template_version === null || detail.template_version === undefined
      ? templateTitleOrFallback(t, null)
      : `${t("company:submissions.detail.version")} ${String(detail.template_version)}`;

  return (
    <div className="p-8 print:p-0">
      <div className="print:hidden">
        <PageHeader
          title={t("company:submissions.detail.title")}
          actions={
            <Button variant="secondary" className="min-h-11" onClick={() => window.print()}>
              {t("company:submissions.detail.print")}
            </Button>
          }
        />
        <div className="mt-2">
          <Link to="/company/submissions" className="text-sm text-primary hover:underline">
            {t("company:submissions.detail.back")}
          </Link>
        </div>
      </div>

      <div className="mt-6 space-y-6 print:mt-0 print:space-y-0">
        {/* THE REASON IS QUOTED VERBATIM. `rejection_reason` is the reviewer's
            own sentence and the only thing on this page that tells the company
            what came back and why; the fs_chk_rejection CHECK permits a reason
            only while the row is rejected, so a value here always means this
            document came back. The CODE's label and cure sit ABOVE the notes,
            read off the shared rejection family so this portal's copy
            localizes — the same rendering the tester's drill-in renders, never
            a second one — and a code the CHECK admits as null leaves the notes
            alone. No Resubmit: that verb is the performing tester's. */}
        {detail.status === "rejected" ? (
          <div role="alert" className="rounded-lg bg-red-100 p-4 print:hidden dark:bg-red-900">
            <p className="text-sm font-semibold text-red-800 dark:text-red-300">
              {detail.reviewed_at === null || detail.reviewed_at === undefined
                ? t("company:submissions.detail.rejected.title")
                : t("company:submissions.detail.rejected.titleAt", {
                    date: formatDateTime(detail.reviewed_at),
                  })}
            </p>
            {detail.rejection_reason_code ? (
              <p className="mt-1 text-sm font-medium text-red-800 dark:text-red-300">
                {t("form-submissions:review.rejectedWith", {
                  label: t(
                    `form-submissions:rejectionReason.${detail.rejection_reason_code}.label`,
                  ),
                  cure: t(`form-submissions:rejectionReason.${detail.rejection_reason_code}.cure`),
                })}
              </p>
            ) : null}
            <p className="mt-1 text-sm text-red-800 dark:text-red-300">
              {detail.rejection_reason ?? t("field-work:home.rejections.noReason")}
            </p>
          </div>
        ) : null}

        <div className="print:hidden">
          <Card title={t("company:submissions.detail.identityTitle")}>
            <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-muted">{t("company:submissions.detail.template")}</dt>
                <dd className="mt-0.5 text-on-surface">{formTitle}</dd>
              </div>
              <div>
                <dt className="text-muted">{t("company:submissions.detail.version")}</dt>
                <dd className="mt-0.5 text-on-surface">{versionLine}</dd>
              </div>
              <div>
                <dt className="text-muted">{t("company:submissions.detail.facility")}</dt>
                <dd className="mt-0.5 text-on-surface">{detail.facility_name ?? <EmptyValue />}</dd>
              </div>
              <div>
                <dt className="text-muted">{t("company:submissions.detail.deviceSerial")}</dt>
                <dd className="mt-0.5 text-on-surface">
                  {detail.device_serial_number ? (
                    <span className="font-mono text-xs">{detail.device_serial_number}</span>
                  ) : (
                    <EmptyValue />
                  )}
                </dd>
              </div>
              {/* The row's own answer to "whose work is this" — the one datum
                  the tester's drill-in never needed, because there the caller
                  is the performer by construction. */}
              <div>
                <dt className="text-muted">{t("company:submissions.detail.tester")}</dt>
                <dd className="mt-0.5 text-on-surface">
                  {detail.performer_name ?? <EmptyValue />}
                </dd>
              </div>
              <div>
                <dt className="text-muted">{t("company:submissions.detail.performedAt")}</dt>
                <dd className="mt-0.5 text-on-surface">
                  {formatPerformedAt(detail.performed_at, detail.performed_date)}
                </dd>
              </div>
              <div>
                <dt className="text-muted">{t("company:submissions.detail.receivedAt")}</dt>
                <dd className="mt-0.5 text-on-surface">{formatDateTime(detail.received_at)}</dd>
              </div>
              <div>
                <dt className="text-muted">{t("company:submissions.columns.status")}</dt>
                <dd className="mt-0.5">
                  <Badge variant={portalStatusVariant(detail.status)}>
                    {t(`field-work:history.status.${detail.status}`)}
                  </Badge>
                </dd>
              </div>
              {detail.outcome && (
                <div>
                  <dt className="text-muted">{t("company:submissions.detail.outcome")}</dt>
                  <dd className="mt-0.5">
                    <Badge variant={submissionOutcomeVariant[detail.outcome]}>
                      {t(submissionOutcomeLabelKey(detail.target_kind, detail.outcome))}
                    </Badge>
                  </dd>
                </div>
              )}
            </dl>
            {/* The persisted acceptance-requirements evaluation, as the server
                wrote it: each checked requirement's label and its OWN cure
                sentence, quoted verbatim — never re-derived here. The cure is
                empty unless the requirement failed, so a failing entry is the
                one that says WHY this document routed the way it did, and it
                carries the red the rejection banner above already taught. */}
            {(detail.requirements ?? []).length > 0 && (
              <ul className="mt-4 space-y-1 border-t border-gray-200 pt-3 text-sm dark:border-gray-700">
                {detail.requirements?.map((requirement) => (
                  <li
                    key={requirement.id}
                    className={requirement.cure ? "text-danger" : "text-muted"}
                  >
                    {requirement.cure
                      ? `${t(`field-work:requirements.${requirement.id}`)}: ${requirement.cure}`
                      : t(`field-work:requirements.${requirement.id}`)}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        {/* The water system's printed form for this test. An accepted test is
            the only one it answers for; any other state is a 404 on the list. */}
        {detail.status === "accepted" ? (
          <CompanyReportFormCard companyId={companyId} submissionId={submissionId} />
        ) : null}

        {/* PRINTS, unlike the chrome around it. An incomplete evidence set
            leaves the row at `submitted` with a null outcome — the resting
            state of a complete document awaiting review — so nothing else on
            the page or the sheet distinguishes the two. A printed record that
            does not disclose it would be the only artifact of this document
            that lies. */}
        {!detail.attachments_complete && (
          <div role="status" className="rounded-lg bg-amber-100 p-3 dark:bg-amber-900">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
              {t("company:submissions.detail.evidence.incompleteTitle")}
            </p>
            <p className="mt-1 text-xs text-amber-800 dark:text-amber-300">
              {t("company:submissions.detail.evidence.incompleteBody", {
                uploaded: detail.attachments.length,
                expected: detail.expected_attachment_count,
              })}
            </p>
          </div>
        )}

        <div
          className="form-doc overflow-clip rounded-xl border border-[var(--form-card-border)] shadow-sm print:rounded-none print:border-0 print:shadow-none"
          data-form-theme={resolveFormTheme(definition)}
        >
          {/* `logoUrl` is pinned null: the org-logo endpoint authenticates as an
              org member and a company member is not one — the testing company
              and the water system are different parties — so the request would
              be a guaranteed 403. The monogram is the designed identity here. */}
          <FormLetterhead
            orgName={detail.org_name ?? ""}
            programLine={t("form-renderer:letterhead.programLine")}
            logoUrl={null}
            formTitle={formTitle}
            metaLine={versionLine}
          />
          <div className="bg-[var(--form-canvas)] p-4 sm:p-6">
            {renderable ? (
              <FormRenderer
                definition={definition}
                answers={detail.responses}
                language={language}
                attachmentSlot={attachmentSlot}
              />
            ) : (
              <p className="text-sm text-muted">
                {t("company:submissions.detail.documentUnavailable")}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
